import { PatientOverview } from "@/types";
import {
  Box,
  Card,
  CardBody,
  Heading,
  Image,
  Text,
  Flex,
  Icon,
  Avatar,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Calendar1, MapPin, MoreVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface PatientsCardViewProps {
  pat: PatientOverview;
  isListView?: boolean;
}

const capitalizeFirstLetter = (str: string | null | undefined): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const PatientsCardView = ({
  pat,
  isListView = false,
}: PatientsCardViewProps) => {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const calendarSize = useBreakpointValue({ base: 12, md: 14 });
  const mapPinSize = useBreakpointValue({ base: 12, md: 14 });

  const formattedGender = capitalizeFirstLetter(pat.gender);

  // List View - Table-like row design
  if (isListView) {
    return (
      <Flex
        className="cursor-pointer"
        onClick={() => navigate(`/patientprofile/${pat.id}`)}
        align="center"
        gap={{ base: 2, md: 4 }}
        borderBottom="1px"
        borderColor="gray.200"
        py={2}
        px={{ base: 3, md: 6 }}
        _hover={{ bg: "gray.50" }}
        transition="all 0.2s"
        _last={{ borderBottom: "none" }}
        bg="white"
        flexDirection={{ base: "column", md: "row" }}
        alignItems={{ base: "flex-start", md: "center" }}
        minH="65px"
      >
        {/* Patient Column */}
        <Flex
          align="center"
          gap={2}
          flex={{ base: "1", md: "2" }}
          minW={{ base: "100%", md: "250px" }}
          mb={{ base: 1, md: 0 }}
        >
          <Avatar
            size="sm"
            name={pat.user_fullname}
            src={pat.profile_picture_url || "/assets/img/profile.jpg"}
            bg="gray.200"
          />
          <Box>
            <Text
              fontSize="14px"
              fontWeight="500"
              color="gray.900"
              mb={0.5}
              lineHeight="tight"
            >
              {pat.user_fullname}
            </Text>
            <Text fontSize="xs" color="gray.600" lineHeight="tight">
              {pat.user_phone_number || "-"}
            </Text>
          </Box>
        </Flex>

        {/* Email Column  */}
        <Box
          flex={{ base: "1", md: "1.5" }}
          minW={{ base: "auto", md: "180px" }}
          display={{ base: "none", lg: "block" }}
        >
          <Text fontSize="sm" color="gray.700" lineHeight="tight">
            {pat.user_email || "-"}
          </Text>
        </Box>

        {/* Age & Gender Column */}
        <Box
          flex={{ base: "1", md: "1" }}
          minW={{ base: "auto", md: "140px" }}
          alignSelf={{ base: "flex-start", md: "center" }}
        >
          <Text fontSize="sm" color="gray.700" lineHeight="tight">
            {pat.age_gender ? pat.age_gender.replace('/', ',') : 'N/A'}
          </Text>
        </Box>

        {/* Last Appointment Column */}
        <Box
          flex={{ base: "1", md: "1.5" }}
          minW={{ base: "auto", md: "200px" }}
          alignSelf={{ base: "flex-start", md: "center" }}
        >
          <Flex align="center" gap={1}>
            <Calendar1 size={14} color="#6B7280" />
            <Text fontSize="sm" color="gray.700" lineHeight="tight">
              {pat.appointments?.[0]?.appointment_date || "-"}
            </Text>
          </Flex>
        </Box>
      </Flex>
    );
  }

  // Grid View - Card design
  return (
    <Card
      className="cursor-pointer"
      onClick={() => navigate(`/patientprofile/${pat.id}`)}
      variant="outline"
      w="100%"
      h="auto"
      position="relative"
      bg="white"
      _hover={{ boxShadow: "md" }}
      transition="all 0.2s"
    >
      <CardBody p={{ base: 3, md: 4 }}>
        {/* Header with Avatar and Menu */}
        <Flex justify="space-between" align="flex-start" mb={3}>
          <Flex align="center" gap={3}>
            <Image
              objectFit="cover"
              w={{ base: "36px", md: "44px" }}
              h={{ base: "36px", md: "44px" }}
              rounded="full"
              src={pat.profile_picture_url || "/assets/img/profile.jpg"}
              alt={pat.user_fullname}
            />
            <Box>
              <Heading size="xs" mb={1} fontWeight="600">
                {pat.user_fullname}
              </Heading>
              <Text fontSize="2xs" color="gray.600">
                {pat.age_gender ? pat.age_gender.replace('/', ',') : 'N/A'}
              </Text>
            </Box>
          </Flex>

          <Icon
            as={MoreVertical}
            boxSize={{ base: 4, md: 5 }}
            cursor="pointer"
            color="gray.400"
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </Flex>

        {/* Last Appointment */}
        <Flex align="center" gap={2} mb={2}>
          <Calendar1 size={calendarSize} color="#6B7280" />
          <Text fontSize="2xs" color="gray.600">
            Last Appointment : {pat.last_appointment_formatted || "N/A"}
          </Text>
        </Flex>

        {/* Address */}
        <Flex align="center" gap={2}>
          <MapPin size={mapPinSize} color="#6B7280" />
          <Text fontSize="2xs" color="gray.600" noOfLines={1}>
            {pat.addresses?.primary
              ? `${pat.addresses.primary.city || ''}, ${pat.addresses.primary.state || ''}, ${pat.addresses.primary.pincode || ''}`
              : "-"}
          </Text>
        </Flex>
      </CardBody>
    </Card>
  );
};

export default PatientsCardView;
